import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User } from '../../services/authService';
import { financeAssetsService, FinanceAsset, AssetLog } from '../../services/financeAssetsService';
import './AssetDetail.css';

interface AssetDetailProps {
  user: User | null;
  onToggleSidebar: () => void;
}

const AssetDetail: React.FC<AssetDetailProps> = ({ user, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [asset, setAsset] = useState<FinanceAsset | null>(null);
  const [logs, setLogs] = useState<AssetLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddValuation, setShowAddValuation] = useState(false);
  const [newValuation, setNewValuation] = useState({
    value: 0,
    valuation_date: financeAssetsService.getCurrentDate(),
    notes: ''
  });

  useEffect(() => {
    if (user?.id && id) {
      loadAssetData();
    }
  }, [user, id]);

  const loadAssetData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      // Load asset details
      const assetResponse = await financeAssetsService.getFinanceAssetById(id);
      if (assetResponse.success && assetResponse.asset) {
        setAsset(assetResponse.asset);
      } else {
        setError('Failed to load asset details');
        return;
      }

      // Load asset logs
      const logsResponse = await financeAssetsService.getAssetLogs(id, 50);
      if (logsResponse.success && logsResponse.logs) {
        setLogs(logsResponse.logs);
      }
    } catch (err) {
      console.error('Error loading asset data:', err);
      setError('Failed to load asset data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddValuation = async () => {
    if (!id || !asset || newValuation.value <= 0) return;

    try {
      const valuationMonth = newValuation.valuation_date.substring(0, 7) + '-01';
      
      // Use full update method to support notes
      const response = await financeAssetsService.updateFinanceAsset({
        id: id,
        value: newValuation.value,
        valuation_date: newValuation.valuation_date,
        valuation_month: valuationMonth,
        notes: newValuation.notes || asset.notes // Keep existing notes if no new notes provided
      });

      if (response.success) {
        setNewValuation({
          value: 0,
          valuation_date: financeAssetsService.getCurrentDate(),
          notes: ''
        });
        setShowAddValuation(false);
        await loadAssetData();
      } else {
        setError(response.message || 'Failed to add valuation');
      }
    } catch (err) {
      console.error('Error adding valuation:', err);
      setError('Failed to add valuation');
    }
  };

  const getCategoryIcon = (category: string) => {
    const icons: { [key: string]: string } = {
      stocks: '📈',
      bonds: '📋',
      real_estate: '🏠',
      commodities: '🥇',
      gold: '🥇',
      crypto: '₿',
      cash: '💵',
      retirement: '🏦',
      insurance: '🛡️',
      collectibles: '🎨',
      business: '🏢',
      other: '📦',
      mortgage: '🏠',
      credit_card: '💳',
      personal_loan: '💰',
      student_loan: '🎓',
      auto_loan: '🚗',
      business_loan: '🏢',
      other_debt: '📋'
    };
    return icons[category] || '📦';
  };

  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      stocks: 'Stocks',
      bonds: 'Bonds',
      real_estate: 'Real Estate',
      commodities: 'Commodities',
      gold: 'Gold',
      crypto: 'Cryptocurrency',
      cash: 'Cash',
      retirement: 'Retirement',
      insurance: 'Insurance',
      collectibles: 'Collectibles',
      business: 'Business',
      other: 'Other Assets',
      mortgage: 'Mortgage',
      credit_card: 'Credit Card',
      personal_loan: 'Personal Loan',
      student_loan: 'Student Loan',
      auto_loan: 'Auto Loan',
      business_loan: 'Business Loan',
      other_debt: 'Other Debt'
    };
    return labels[category] || 'Other';
  };

  const isDebtCategory = (category: string): boolean => {
    return ['mortgage', 'credit_card', 'personal_loan', 'student_loan', 'auto_loan', 'business_loan', 'other_debt'].includes(category);
  };

  if (loading) {
    return (
      <div className="asset-detail-container">
        <div className="ad-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <h1>Loading...</h1>
          <button className="ad-back-btn" onClick={() => navigate('/finance/assets')}>
            ← Back
          </button>
        </div>
        <div className="ad-loading-state">
          <div className="ad-loading-spinner"></div>
          <p>Loading asset details...</p>
        </div>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="asset-detail-container">
        <div className="ad-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <h1>Error</h1>
          <button className="ad-back-btn" onClick={() => navigate('/finance/assets')}>
            ← Back
          </button>
        </div>
        <div className="ad-error-state">
          <p>{error || 'Asset not found'}</p>
          <button onClick={() => navigate('/finance/assets')} className="ad-back-to-list-btn">
            Back to Assets
          </button>
        </div>
      </div>
    );
  }

  const currentValue = asset.value;
  const isDebt = isDebtCategory(asset.category);

  return (
    <div className="asset-detail-container">
      <div className="ad-header">
        <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
        <h1>{getCategoryIcon(asset.category)} {asset.name}</h1>
        <button className="ad-back-btn" onClick={() => navigate('/finance/assets')}>
          ← Back
        </button>
      </div>

      {error && (
        <div className="ad-error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="ad-main-content">
        {/* Asset Summary */}
        <div className="ad-summary-section">
          <div className={`ad-value-card ${isDebt ? 'debt' : 'asset'}`}>
            <div className="ad-value-label">Current Value</div>
            <div className="ad-value-amount">
              {isDebt && '-'}
              {financeAssetsService.formatCurrency(currentValue, 'VND')}
            </div>
            <div className="ad-value-meta">
              <span>{getCategoryLabel(asset.category)}</span>
              {asset.sub_category && <span> • {asset.sub_category}</span>}
            </div>
            <div className="ad-value-date">
              Last updated: {new Date(asset.valuation_date).toLocaleDateString()}
            </div>
          </div>

          {asset.notes && (
            <div className="ad-notes-section">
              <h3 className="ad-section-title">Notes</h3>
              <p className="ad-notes-text">{asset.notes}</p>
            </div>
          )}

          <button 
            className="ad-add-valuation-btn"
            onClick={() => setShowAddValuation(!showAddValuation)}
          >
            {showAddValuation ? '✕ Cancel' : '➕ Add Valuation'}
          </button>
        </div>

        {/* Add Valuation Form */}
        {showAddValuation && (
          <div className="ad-add-valuation-form">
            <h3 className="ad-form-title">Add New Valuation</h3>
            <div className="ad-form-row">
              <div className="ad-form-group">
                <label className="ad-form-label">Value</label>
                <input
                  type="number"
                  value={newValuation.value || ''}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    let numValue = 0;
                    
                    if (inputValue !== '') {
                      const parsed = parseFloat(inputValue);
                      numValue = isNaN(parsed) || !isFinite(parsed) ? 0 : Math.abs(parsed);
                    }
                    
                    setNewValuation({ ...newValuation, value: numValue });
                  }}
                  className="ad-form-input"
                  placeholder="Enter value"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="ad-form-group">
                <label className="ad-form-label">Date</label>
                <input
                  type="date"
                  value={newValuation.valuation_date}
                  onChange={(e) => setNewValuation({ ...newValuation, valuation_date: e.target.value })}
                  className="ad-form-input"
                />
              </div>
            </div>
            <div className="ad-form-group">
              <label className="ad-form-label">Notes (optional)</label>
              <textarea
                value={newValuation.notes}
                onChange={(e) => setNewValuation({ ...newValuation, notes: e.target.value })}
                className="ad-form-textarea"
                placeholder="Add notes about this valuation..."
                rows={2}
              />
            </div>
            <div className="ad-form-actions">
              <button 
                onClick={handleAddValuation}
                className="ad-save-btn"
                disabled={newValuation.value <= 0}
              >
                Save Valuation
              </button>
              <button 
                onClick={() => {
                  setNewValuation({
                    value: 0,
                    valuation_date: financeAssetsService.getCurrentDate(),
                    notes: ''
                  });
                  setShowAddValuation(false);
                }}
                className="ad-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Valuation History */}
        <div className="ad-history-section">
          <h3 className="ad-section-title">Valuation History</h3>
          {logs.length === 0 ? (
            <div className="ad-empty-logs">
              <p>No valuation history yet</p>
            </div>
          ) : (
            <div className="ad-logs-list">
              {logs.map((log) => {
                const formattedLog = financeAssetsService.formatAssetLog(log);
                return (
                  <div key={log.id} className={`ad-log-item ${formattedLog.type}`}>
                    <div className="ad-log-header">
                      <span className="ad-log-date">{new Date(log.valuation_date).toLocaleDateString()}</span>
                      {formattedLog.change_display && (
                        <span className={`ad-log-change ${formattedLog.type}`}>
                          {formattedLog.change_display}
                        </span>
                      )}
                    </div>
                    <div className="ad-log-body">
                      <div className="ad-log-value">{formattedLog.value_display}</div>
                      {log.notes && (
                        <div className="ad-log-notes">{log.notes}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetDetail;

